// Internal dependencies
import { Component, OnInit } from '@angular/core';

// External dependencies

// Modules

// Components

// Services
import { DialogService } from '@/services/dialog.service';
import { FlashMessageService } from '@/services/flash-message.service';
import { LoginService } from '@/services/login.service';
import { LoadingService } from '@/services/loading.service';
import { StructuresService } from '@/services/structures.service';
import { UsersService } from '@/services/users.service';
import { ApplicationService } from '@/services/application.service';

// Interfaces

// Pipes

// Interface

import { Application, ApplicationAccess } from '@/structures/application';
import { Role } from '@/structures/role';
import { Structure } from '@/structures/structure';

@Component({
    selector: 'app-rights',
    templateUrl: './rights.component.html',
    styleUrls: ['./rights.component.scss'],
    standalone: false
})
export class RightsComponent implements OnInit {
    constructor(
        private applicationService: ApplicationService,
        private structuresService: StructuresService,
        private loginService: LoginService,
        private loadingService: LoadingService,
        private flashMessageService: FlashMessageService,
        private usersService: UsersService,
        private dialogService: DialogService
    ) {}

    structures: Array<Structure>;

    roles: Array<Role>;
    additionalRoles: Array<any>;

    additionalRolesAccess: Array<any>;
    applicationsAccess: Array<ApplicationAccess>;

    ngOnInit(): void {
        this.loadingService.startLoading('rights', 'getStructures');
        this.structuresService.structures$.subscribe((structuresFromService: Structure[]) => {
            this.loadingService.stopLoading('rights', 'getStructures');
            this.structures = structuresFromService.map((st) => ({
                ...st,
                selected: this.loginService.getUser().structureid === st.id
            }));
            this.handleStructureChange();
        });

        this.loadingService.startLoading('rights', 'userTypes');
        this.usersService.getRoles().subscribe((userTypes: Role[]) => {
            this.roles = userTypes;
            this.loadingService.stopLoading('rights', 'userTypes');
        });
        this.usersService.getAdditionalRoles().subscribe((additionalRoles: Array<any>) => {
            this.additionalRoles = additionalRoles;
        });
    }

    // #region Getters

    getSelectedStructure(): Structure | undefined {
        if (this.structures && this.structures.some((i) => i.selected)) {
            return this.structures.find((item) => item.selected);
        }
        return undefined;
    }

    getApplicationIcon(application: Application): string {
        if (application.app_id === 'easitraining') {
            return 'icon-formateurinterne';
        } else if (application.app_id === 'easilearning') {
            return 'icon-easi-training-line';
        } else if (application.app_id === 'easicall' || application.app_id === 'phone') {
            return 'icon-easi-call-line';
        } else if (
            application.app_id === 'easicloud' ||
            application.app_id.includes(`easi_ncv2_${this.getSelectedStructure().shortname}`)
        ) {
            return 'icon-easi-cloud-line';
        } else if (application.app_id === 'easiforum') {
            return 'icon-easi-forum-line';
        } else if (application.app_id === 'easichat' || application.app_id === 'chat') {
            return 'icon-Messages';
        } else if (application.app_id === 'machinepaysdelaloire') {
            return 'icon-logoeasi';
        } else if (application.app_id === 'msoffice') {
            return 'icon-Office365';
        } else if (application.app_id === 'orthodidacte_formateur') {
            return 'icon orthodidacte';
        }
    }

    getApplicationColor(application: Application): string {
        if (application.app_id === 'easitraining') {
            return '#007FAD';
        } else if (application.app_id === 'easilearning') {
            return '#199fe2';
        } else if (application.app_id === 'easicall' || application.app_id === 'phone') {
            return '#f36';
        } else if (
            application.app_id === 'easicloud' ||
            application.app_id.includes(`easi_ncv2_${this.getSelectedStructure().shortname}`)
        ) {
            return '#e8b600';
        } else if (application.app_id === 'easiforum') {
            return '#7f4482';
        } else if (application.app_id === 'easichat' || application.app_id === 'chat') {
            return '#102c3a';
        } else if (application.app_id === 'machinepaysdelaloire') {
            return '#424242';
        } else if (application.app_id === 'msoffice') {
            return '#dc5928';
        } else if (application.app_id === 'orthodidacte_formateur') {
            return '#424242';
        }
    }

    isNationalAdmin(): boolean {
        if (this.loginService.getUser()) {
            return this.loginService.getUser().roles.nationalAdmin;
        }
    }

    getMainStyles(applicationsList) {
        if (applicationsList) {
            return {
                'grid-template-columns': `auto repeat(${applicationsList.length}, auto)`,
                'grid-template-rows': `auto`
            };
        }
    }

    getApplicationIconClass(application: any, role: Role): string[] {
        return [
            this.hasApplicationAccess(application, role) ? 'icon-selected' : 'icon-select',
            this.isApplicationAccessLocked(application, role) ? 'locked' : ''
        ];
    }

    getAdditionalRoleIconClass(role: any, additionalRole: Role): string[] {
        return [
            this.hasAdditionalRole(role, additionalRole) ? 'icon-selected' : 'icon-select',
            this.isAdditionalRoleLocked(role, additionalRole) ? 'locked' : ''
        ];
    }

    hasExceptionForRole(application: any, role: Role): boolean {
        for (const i in application.users) {
            if (application.users[i].roles[role.shortname]) {
                return true;
            }
        }
        return false;
    }

    showRole(role: Role): boolean {
        if (
            role.shortname === 'validator' ||
            role.shortname === 'contentManager' ||
            role.shortname === 'accountManager' ||
            role.shortname === 'nationalAdmin' ||
            role.shortname === 'localAdmin' ||
            role.shortname === 'externalCallManager'
        ) {
            return false;
        } else if (
            role.shortname === 'siteTeacher' &&
            this.loginService.getUser().roles.localAdmin
        ) {
            return false;
        } else if (
            role.shortname === 'nationalTeacher' &&
            (this.loginService.getUser().roles.localAdmin ||
                this.getSelectedStructure().shortname !== 'N4S')
        ) {
            return false;
        } else {
            return true;
        }
    }

    isLoading() {
        return this.loadingService.isLoading('rights');
    }

    // #endregion Getters

    // #region Handlers

    handleStructureChange(): void {
        if (this.getSelectedStructure()?.key) {
            this.loadingService.startLoading('rights', 'getApplicationsAccess');
            this.loadingService.startLoading('rights', 'getAdditionalRoleAccess');

            this.applicationService
                .getApplicationsAccess(this.getSelectedStructure().key)
                .subscribe((data: ApplicationAccess[]) => {
                    this.loadingService.stopLoading('rights', 'getApplicationsAccess');
                    this.applicationsAccess = data.filter((application: ApplicationAccess) => {
                        return (
                            ([
                                'easitraining',
                                'easilearning',
                                'easicall',
                                'easicloud',
                                'easiforum',
                                'easichat',
                                'machinepaysdelaloire',
                                'msoffice',
                                'chat',
                                'phone',
                                'orthodidacte_formateur'
                            ].indexOf(application.app_id) > -1 ||
                                (application.app_id.includes(
                                    `easi_ncv2_${this.getSelectedStructure().shortname}`
                                ) &&
                                    application.app_id !== 'easi_ncv2_transverse')) &&
                            this.canShowApplication(application)
                        );
                    });
                });

            this.applicationService
                .getAdditionalRolesAccess(this.getSelectedStructure().key)
                .subscribe((data: ApplicationAccess[]) => {
                    this.additionalRolesAccess = data;
                    this.loadingService.stopLoading('rights', 'getAdditionalRoleAccess');
                });
        }
    }

    handleClickViewExceptions($event: MouseEvent, application: any, role?: Role): void {
        $event.stopImmediatePropagation();
        this.dialogService.openRightsExceptions(application, role);
    }

    handleClickToggleAdditionalRole(role: Role, additionalRole: any): void {
        if (!this.isAdditionalRoleLocked(role, additionalRole)) {
            const params = {
                structureid: this.getSelectedStructure().key,
                type: 'role',
                instance: role.shortname,
                additional_role_id: additionalRole.id,
                allow: !this.hasAdditionalRole(role, additionalRole)
            };
            this.applicationService
                .setAdditionalRolesAccess(params)
                .subscribe((response: boolean) => {
                    this.flashMessageService.flash(
                        'Les droits des utilisateurs ont été mis à jour.'
                    );
                    for (const i in additionalRole.roles) {
                        if (additionalRole.roles[i].role === role.shortname) {
                            additionalRole.roles[i].allow = params.allow;
                        }
                    }
                });
        }
    }

    handleClickToggleApplicationAccess(application: Application, role: Role): void {
        if (!this.isApplicationAccessLocked(application, role)) {
            const params = {
                structureid: this.getSelectedStructure().key,
                type: 'role',
                instance: role.shortname,
                applicationid: application.id,
                allow: !this.hasApplicationAccess(application, role)
            };
            this.applicationService.setApplicationAccess(params).subscribe((response: boolean) => {
                this.flashMessageService.flash('Les droits des utilisateurs ont été mis à jour.');
                for (const i in application.roles) {
                    if (application.roles[i].role === role.shortname) {
                        application.roles[i].allow = params.allow;
                    }
                }
            });
        }
    }

    // #endregion Handlers

    // #region Internals

    private hasApplicationAccess(application: any, role: Role): boolean {
        for (const i in application.roles) {
            if (
                application.roles[i].role === role.shortname &&
                application.roles[i].allow !== null
            ) {
                return application.roles[i].allow;
            }
        }
        for (const j in application.general.roles) {
            if (
                application.general.roles[j].role === role.shortname &&
                application.general.roles[j].allow !== null
            ) {
                return application.general.roles[j].allow;
            }
        }
        if (application.structure.allow !== null) {
            return application.structure.allow;
        }
        return application.general.default.allow;
    }

    private isApplicationAccessLocked(application: any, role: Role): boolean {
        for (const i in application.roles) {
            if (
                application.roles[i].role === role.shortname &&
                application.roles[i].locked !== null
            ) {
                return application.roles[i].locked;
            }
        }
        for (const j in application.general.roles) {
            if (
                application.general.roles[j].role === role.shortname &&
                application.general.roles[j].locked !== null
            ) {
                return application.general.roles[j].locked;
            }
        }
        if (application.structure.locked !== null) {
            return application.structure.locked;
        }
        return application.general.default.locked;
    }

    private hasAdditionalRole(role: Role, additionalRole: any): boolean {
        for (const i in additionalRole.roles) {
            if (
                additionalRole.roles[i].role === role.shortname &&
                additionalRole.roles[i].allow !== null
            ) {
                return additionalRole.roles[i].allow;
            }
        }
        for (const j in additionalRole.general.roles) {
            if (
                additionalRole.general.roles[j].role === role.shortname &&
                additionalRole.general.roles[j].allow !== null
            ) {
                return additionalRole.general.roles[j].allow;
            }
        }
        if (additionalRole.structure.allow !== null) {
            return additionalRole.structure.allow;
        }
        return additionalRole.general.default.allow;
    }

    private isAdditionalRoleLocked(role: Role, additionalRole: any): boolean {
        for (const i in additionalRole.roles) {
            if (
                additionalRole.roles[i].role === role.shortname &&
                additionalRole.roles[i].locked !== null
            ) {
                return additionalRole.roles[i].locked;
            }
        }
        for (const j in additionalRole.general.roles) {
            if (
                additionalRole.general.roles[j].role === role.shortname &&
                additionalRole.general.roles[j].locked !== null
            ) {
                return additionalRole.general.roles[j].locked;
            }
        }
        if (additionalRole.structure.locked !== null) {
            return additionalRole.structure.locked;
        }
        return additionalRole.general.default.locked;
    }

    private canShowApplication(application): boolean {
        if (
            application.general.default.allow === true ||
            application.general.default.locked === false
        ) {
            return true;
        }

        if (application.structure.allow === true || application.structure.locked === false) {
            return true;
        }

        for (const i in application.general.roles) {
            if (
                application.general.roles[i].allow === true ||
                application.general.roles[i].locked === false
            ) {
                return true;
            }
        }

        for (const j in application.roles) {
            if (application.roles[j].allow === true || application.roles[j].locked === false) {
                return true;
            }
        }

        for (const j in application.roles) {
            if (application.roles[j].allow === true || application.roles[j].locked === false) {
                return true;
            }
        }

        for (const k in application.users) {
            if (application.users[k].allow === true || application.users[k].locked === false) {
                return true;
            }
        }

        for (const l in application.groups) {
            if (application.groups[l].allow === true || application.groups[l].locked === false) {
                return true;
            }
        }

        return false;
    }

    // #endregion Internals
}
